import { Info, Video } from "lucide-react";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { Card } from "@/components/ui/card";
import { Tooltip as UITooltip } from "@/components/ui/tooltip";
import { formatNumber } from "@/utils/format";

interface TopVideo {
  videoId: string;
  rank: number;
  views: number;
  medianMultiplier: number;
  title?: string;
  url: string;
  platform: string;
  creator: {
    name: string;
    followers: number;
  };
}

interface TopVideosProps {
  isLoading: boolean;
  videos?: TopVideo[];
}

export function TopVideos({ isLoading, videos }: TopVideosProps) {
  return (
    <Card className="p-6 min-h-[400px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Video className="h-5 w-5 text-primary/70" />
          <h3 className="text-lg font-semibold text-primary">
            Top Videos (Approved)
          </h3>
          <UITooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                Shows the best performing approved videos ranked by total views.
              </p>
            </TooltipContent>
          </UITooltip>
        </div>
        <p className="text-sm text-primary/60 mb-4">
          Highest performing approved videos
        </p>
      </div>

      {isLoading ? (
        <div className="h-48 bg-gray-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-primary/60">Loading top videos...</div>
        </div>
      ) : videos && videos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {videos.map((video) => (
            <div
              key={video.videoId}
              className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3"
            >
              <div className="space-y-1">
                <div className="text-lg font-bold text-primary">
                  #{video.rank} = {formatNumber(video.views)} views
                </div>
                <div className="text-sm font-semibold text-primary/70">
                  {video.medianMultiplier}x Median
                </div>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate"
                  title={video.title || "Video"}
                >
                  {video.title
                    ? video.title.length > 40
                      ? `${video.title.substring(0, 40)}...`
                      : video.title
                    : "Untitled Video"}
                </a>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-primary/60">
                  by <span className="font-medium">{video.creator.name}</span>
                </div>
                <div className="text-sm text-primary/60">
                  {video.creator.followers > 0
                    ? `${formatNumber(video.creator.followers)} followers`
                    : "Followers: N/A"}
                </div>
                <div className="text-sm font-medium text-primary/80 capitalize">
                  {video.platform === "youtube-shorts"
                    ? "YT Short"
                    : video.platform === "youtube"
                      ? "YT Long"
                      : video.platform}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            No Videos Found
          </h3>
          <p className="text-sm text-primary/60">
            No approved videos found in the selected campaigns.
          </p>
        </div>
      )}
    </Card>
  );
}
